/**
 * THE COMMODITY VOCABULARY OF THE BRAIN CORE WEB APPLICATION (BRN-2282 / BRN-2283 AC4).
 *
 * ONE DEFINITION, CONSUMED EVERYWHERE. Before this change the five-value list was re-declared across
 * roughly fifty files -- four independent type unions, eight option arrays, three `<option>` blocks,
 * five chip/legend markup blocks, six colour maps, and eight hand-written copies of one bidirectional
 * Title-Case <-> UPPER mapping. None of them was compiler-guarded, and `size-guard` does not measure
 * plain `.ts` in this repository at all (`brain` is in `TS_LADDER_EXEMPT_REPOS`), so a copy that fell
 * behind fell behind silently. THREE OF THEM ALREADY HAD:
 *
 *  1. `UTILITY_ICON` said gas -> `propane_tank` and propane -> `gas_meter`, the exact inverse of what
 *     `shared/pipes/utility-icon.pipe.ts` returned for the same two commodities. One of the two was
 *     wrong on every screen that used it; the pipe's reading is the one kept here, because the icons
 *     are Material Symbols whose own names say which is which.
 *  2. `shared/services/utils/graph/graph.service.ts`'s `getGraphColor2` had no propane case while its
 *     near-twin `shared/pipes/graph-color-alt.pipe.ts` did, so a propane series drew with a null
 *     colour from one path and the right colour from the other.
 *  3. `EnergyType` spells electricity `'Electric'` while everything else spells it `'Electricity'`.
 *     That one is NOT a defect and is NOT unified: `'Electric'` is the wire vocabulary the onboarding
 *     API speaks, so it is carried here as its own field ([UtilityCommodity.energyType]) beside the
 *     display label rather than reconciled away.
 *
 * DERIVED, NOT DUPLICATED. Everything below [UTILITY_COMMODITIES] is computed from it. Adding a
 * commodity means adding one row; every map, union, option list, label and icon follows, and the
 * TypeScript compiler reports any `switch` that has not kept up because the unions widen with the
 * table. `scripts/checks/verify-utility-vocabulary.ts` pins the copies this file cannot reach (the
 * backend enum, the `<option>` markup) BY DISCOVERY rather than by an enumerated list.
 *
 * THE SOURCE OF TRUTH IS THE BACKEND ENUM. `backend/src/main/kotlin/com/idesign/brain/domain/utility/
 * UtilityType.kt` declares the same thirteen members with the same stored names; this table mirrors
 * it and the vocabulary check fails when the two disagree.
 *
 * WHAT A SCREEN MAY OFFER is narrower. BRN-2281 grew the stored vocabulary, and every picker, chip
 * and "not discovered" list followed it. The product offers electricity, gas, solar, water and
 * propane. The later commodities stay in this table so a stored value still has a label, an icon
 * and a colour, and `selectable: false` keeps them out of those lists.
 */

/**
 * Darkens a hex colour by a fixed factor. Used ONLY to derive the secondary series shade of the
 * commodities BRN-2282/2283 added, so that seven alternate colours are computed from one rule rather
 * than seven more values invented and printed as facts. The five original commodities keep the
 * hand-picked shades the product shipped with, byte-identical.
 */
function shade(hex: string, factor = 0.72): string {
  const value = parseInt(hex.slice(1), 16);
  const channel = (offset: number) => Math.round(((value >> offset) & 0xff) * factor);
  const toHex = (component: number) => component.toString(16).padStart(2, '0');
  return `#${toHex(channel(16))}${toHex(channel(8))}${toHex(channel(0))}`;
}

/**
 * One commodity, as every surface in this application needs to know it.
 *
 * `key` is the lower-case identifier this app has always used internally and in `UTILITY_NAME`;
 * `stored` is the value the API sends and accepts (the backend `UtilityType` member name);
 * `energyType` is the Title-Case spelling the onboarding API speaks, which is NOT always the label.
 */
export interface UtilityCommodity {
  readonly key: string;
  readonly stored: string;
  readonly label: string;
  readonly energyType: string;
  readonly icon: string;
  readonly chartColor: string;
  readonly chartAltColor: string;
  readonly usageUnit: string;
  /** The unit a tariff is priced in, or null where nothing is metered so nothing is priced per unit. */
  readonly rateUnit: string | null;
  /**
   * False when a screen must not offer the commodity: the read-side sentinel, and the commodities
   * stored for bills the product does not ask a user to pick. A known non-selectable value still
   * resolves to its label; it is just absent from pickers, chips and onboarding expectations.
   */
  readonly selectable: boolean;
}

/** What a commodity's usage unit says when it is billed by service rather than by a metered quantity. */
export const NOT_METERED = 'Not metered';

export const UTILITY_COMMODITIES = [
  {
    key: 'electricity',
    stored: 'ELECTRICITY',
    label: 'Electricity',
    energyType: 'Electric',
    icon: 'electric_bolt',
    chartColor: '#e9a905',
    chartAltColor: '#b38e2f',
    usageUnit: 'kWh',
    rateUnit: 'kWh',
    selectable: true,
  },
  {
    key: 'gas',
    stored: 'GAS',
    label: 'Gas',
    energyType: 'Gas',
    icon: 'gas_meter',
    chartColor: '#b01ee5',
    chartAltColor: '#6f0096',
    usageUnit: 'MMBtu',
    rateUnit: 'MMBtu',
    selectable: true,
  },
  {
    key: 'solar',
    stored: 'SOLAR',
    label: 'Solar',
    energyType: 'Solar',
    icon: 'solar_power',
    chartColor: '#55a119',
    chartAltColor: '#205e0b',
    usageUnit: 'kWh',
    rateUnit: 'kWh',
    selectable: true,
  },
  {
    key: 'water',
    stored: 'WATER',
    label: 'Water',
    energyType: 'Water',
    icon: 'water_drop',
    chartColor: '#0591bd',
    chartAltColor: '#0064ff',
    usageUnit: 'Gallons',
    rateUnit: 'gallon',
    selectable: true,
  },
  {
    key: 'propane',
    stored: 'PROPANE',
    label: 'Propane',
    energyType: 'Propane',
    icon: 'propane_tank',
    chartColor: '#e47616',
    chartAltColor: '#b85d11',
    usageUnit: 'Gallons',
    rateUnit: 'gallon',
    selectable: true,
  },
  {
    key: 'sewer',
    stored: 'SEWER',
    label: 'Sewer',
    energyType: 'Sewer',
    icon: 'water_damage',
    chartColor: '#1c6f8c',
    chartAltColor: shade('#1c6f8c'),
    usageUnit: 'Gallons',
    rateUnit: 'gallon',
    selectable: false,
  },
  {
    key: 'trash',
    stored: 'TRASH',
    label: 'Trash',
    energyType: 'Trash',
    icon: 'delete',
    chartColor: '#6b7280',
    chartAltColor: shade('#6b7280'),
    usageUnit: NOT_METERED,
    rateUnit: null,
    selectable: false,
  },
  {
    key: 'chilledWater',
    stored: 'CHILLED_WATER',
    label: 'Chilled Water',
    energyType: 'Chilled Water',
    icon: 'ac_unit',
    chartColor: '#2f9fd0',
    chartAltColor: shade('#2f9fd0'),
    usageUnit: 'Ton-Hours',
    rateUnit: 'ton-hour',
    selectable: false,
  },
  {
    key: 'steam',
    stored: 'STEAM',
    label: 'Steam',
    energyType: 'Steam',
    icon: 'heat_pump',
    chartColor: '#b4531f',
    chartAltColor: shade('#b4531f'),
    usageUnit: 'kLb',
    rateUnit: 'klb',
    selectable: false,
  },
  {
    key: 'compressedAir',
    stored: 'COMPRESSED_AIR',
    label: 'Compressed Air',
    energyType: 'Compressed Air',
    icon: 'air',
    chartColor: '#0f8b7e',
    chartAltColor: shade('#0f8b7e'),
    usageUnit: 'MCF',
    rateUnit: 'mcf',
    selectable: false,
  },
  {
    key: 'stormDrainage',
    stored: 'STORM_DRAINAGE',
    label: 'Storm Drainage',
    energyType: 'Storm Drainage',
    icon: 'rainy',
    chartColor: '#4a6fa5',
    chartAltColor: shade('#4a6fa5'),
    usageUnit: NOT_METERED,
    rateUnit: null,
    selectable: false,
  },
  {
    key: 'fireProtection',
    stored: 'FIRE_PROTECTION',
    label: 'Fire Protection',
    energyType: 'Fire Protection',
    icon: 'local_fire_department',
    chartColor: '#8c2f3f',
    chartAltColor: shade('#8c2f3f'),
    usageUnit: NOT_METERED,
    rateUnit: null,
    selectable: false,
  },
  {
    /**
     * THE READ-SIDE SENTINEL, spelled exactly as the backend enum and brain-payments spell it. The
     * API can send it when a row carries a value this build does not define; a screen renders it as
     * an unknown commodity rather than as a blank, and nothing ever offers it for selection.
     */
    key: 'unrecognised',
    stored: 'UNRECOGNISED',
    label: 'Unrecognised',
    energyType: 'Unrecognised',
    icon: 'help',
    chartColor: '#0f172a',
    chartAltColor: shade('#0f172a'),
    usageUnit: NOT_METERED,
    rateUnit: null,
    selectable: false,
  },
] as const satisfies readonly UtilityCommodity[];

type Commodity = (typeof UTILITY_COMMODITIES)[number];

/**
 * A commodity a screen may store or offer: every row except the read-side sentinel.
 *
 * Narrowed as a TYPE, not only filtered at runtime, so a consumer that may never see the sentinel --
 * the onboarding wire vocabulary, a rate editor, a selection list -- is told so by the compiler
 * instead of having to defend against a value it can never receive.
 */
type SelectableCommodity = Extract<Commodity, { selectable: true }>;

/** The lower-case identifier of every commodity — the key `UTILITY_TYPE` and friends are keyed by. */
export type UtilityKey = Commodity['key'];

/** Every value the API stores and accepts, including the read-side sentinel. */
export type UtilityType = Commodity['stored'];

/** The Title-Case vocabulary the onboarding API speaks. */
export type EnergyType = Commodity['energyType'];

/** Every commodity a screen may store or offer — the vocabulary minus the read-side sentinel. */
export type SelectableUtilityType = Exclude<UtilityType, 'UNRECOGNISED'>;

/**
 * The internal key of every commodity a screen may store or offer.
 *
 * This is what a `Record<SelectableUtilityKey, …>` is keyed by, and that is the point: a per-commodity
 * decision a screen has to MAKE rather than derive — which colour family a public card paints in,
 * which emission factor a saving carries — is written as a total map over this union, so adding a
 * commodity to [UTILITY_COMMODITIES] fails the compiler at every such decision instead of silently
 * skipping it.
 */
export type SelectableUtilityKey = SelectableCommodity['key'];

/** The Title-Case vocabulary minus the read-side sentinel. */
export type SelectableEnergyType = Exclude<EnergyType, 'Unrecognised'>;

type FieldMap<F extends keyof Commodity> = {
  [K in UtilityKey]: Extract<Commodity, { key: K }>[F];
};

function fieldMap<F extends keyof Commodity>(field: F): FieldMap<F> {
  return Object.fromEntries(UTILITY_COMMODITIES.map((c) => [c.key, c[field]])) as FieldMap<F>;
}

/** `{ electricity: 'ELECTRICITY', ... }` — the stored value of every commodity, keyed by its key. */
export const UTILITY_TYPE = fieldMap('stored');

/**
 * `{ electricity: 'electricity', ... }` — the lower-case series name, keyed by its key.
 *
 * Historically this was a hand-written identity map over five single words. It is derived now, which
 * is why the multi-word commodities read `chilledWater` rather than `chilled_water`: the key IS the
 * name in this application, and the stored value is the separate `UTILITY_TYPE` entry.
 */
export const UTILITY_NAME = fieldMap('key');

/** `{ electricity: 'electric_bolt', ... }` — the Material Symbol of every commodity. */
export const UTILITY_ICON = fieldMap('icon');

/** `{ electricity: 'Electricity', ... }` — the display label of every commodity. */
export const UTILITY_LABEL = fieldMap('label');

const BY_STORED = new Map<string, UtilityCommodity>(UTILITY_COMMODITIES.map((c) => [c.stored, c]));
const BY_KEY = new Map<string, UtilityCommodity>(UTILITY_COMMODITIES.map((c) => [c.key, c]));
const BY_ENERGY_TYPE = new Map<string, UtilityCommodity>(
  UTILITY_COMMODITIES.map((c) => [c.energyType, c])
);

/** The commodities a screen may offer — everything except the read-side sentinel. */
export const SELECTABLE_UTILITIES: readonly SelectableCommodity[] = UTILITY_COMMODITIES.filter(
  (c): c is SelectableCommodity => c.selectable
);

/** Commodities whose header chip toggles the dashboard graph. The rest stay out of this row. */
const GRAPH_HEADER_UTILITY_KEYS = ['electricity', 'gas', 'solar', 'water', 'propane'] as const;

export function graphHeaderUtilities(includePropane: boolean): readonly SelectableCommodity[] {
  return SELECTABLE_UTILITIES.filter(commodity => {
    if (!(GRAPH_HEADER_UTILITY_KEYS as readonly string[]).includes(commodity.key)) {
      return false;
    }
    return commodity.key !== 'propane' || includePropane;
  });
}

/**
 * Resolves a commodity from any spelling this application handles — the stored value, the internal
 * key, or the onboarding API's Title-Case name — case-insensitively, returning undefined for a value
 * no row claims. Every mapping helper below is one line on top of this, so there is one resolution
 * rule rather than one per call site.
 */
export function findUtility(value: string | null | undefined): UtilityCommodity | undefined {
  if (!value) {
    return undefined;
  }
  const raw = value.trim();
  const direct = BY_STORED.get(raw.toUpperCase()) ?? BY_KEY.get(raw) ?? BY_ENERGY_TYPE.get(raw);
  if (direct) {
    return direct;
  }
  const folded = raw.toLowerCase().replace(/[\s_-]+/g, '');
  return UTILITY_COMMODITIES.find((c) => c.key.toLowerCase() === folded);
}

/** The display label of a commodity, or the value itself when nothing claims it. */
export function utilityLabel(value: string | null | undefined): string {
  return findUtility(value)?.label ?? (value ?? '');
}

/** The Material Symbol of a commodity, or an empty string when nothing claims it. */
export function utilityIcon(value: string | null | undefined): string {
  return findUtility(value)?.icon ?? '';
}

/**
 * The stored value of a commodity a screen may act on, or undefined when nothing selectable claims
 * the value — the read-side sentinel included, since no action accepts it.
 *
 * Replaces the five-arm `name -> UTILITY_TYPE.x` switches the report-download paths carried.
 */
export function selectableUtilityType(value: string | null | undefined): SelectableUtilityType | undefined {
  const commodity = findUtility(value);
  return commodity?.selectable ? (commodity.stored as SelectableUtilityType) : undefined;
}

/**
 * The stored value for a Title-Case onboarding name — `'Electric'` to `'ELECTRICITY'`.
 *
 * Replaces eight hand-written copies of this mapping. It falls back to `ELECTRICITY` for an unknown
 * name because that is what every one of those copies did in its `default:` arm; the fallback is
 * preserved rather than tightened so this consolidation changes no behaviour it was not asked to.
 */
export function energyTypeToUtilityType(value: string | null | undefined): SelectableUtilityType {
  const found = findUtility(value);
  if (found && found.stored !== 'UNRECOGNISED') {
    return found.stored as SelectableUtilityType;
  }
  return UTILITY_TYPE.electricity as SelectableUtilityType;
}

/**
 * The Title-Case onboarding name for a stored value — `'ELECTRICITY'` to `'Electric'`.
 *
 * The `'Electric'` fallback for an unknown value is the `default:` arm every hand-written copy of
 * this mapping carried, preserved rather than tightened.
 */
export function utilityTypeToEnergyType(value: string | null | undefined): SelectableEnergyType {
  const found = findUtility(value);
  if (found && found.energyType !== 'Unrecognised') {
    return found.energyType as SelectableEnergyType;
  }
  return 'Electric';
}

/** One option row for a `<select>`, a chip list or a legend, derived so no screen keeps its own. */
export interface UtilityOption {
  readonly value: SelectableUtilityType;
  readonly label: string;
  readonly icon: string;
  readonly unit: string | null;
  readonly key: UtilityKey;
}

/** Every commodity a screen may offer, in the enum's own order. */
export const UTILITY_OPTIONS: readonly UtilityOption[] = SELECTABLE_UTILITIES.map((c) => ({
  value: c.stored,
  label: c.label,
  icon: c.icon,
  unit: c.rateUnit,
  key: c.key,
}));

export const UTILITY_BILLS_STATUS = {
  completed: 'COMPLETED',
  partial: 'PARTIAL',
  failed: 'FAILED',
} as const;

export const UTILITY_BILLS_PROPS = {
  username: 'system@gridwatchems.com',
};

export const UTILITY_UNIT = {
  kwh: 'kWh',
  gallons: 'Gallons',
  mmbtu: 'MMBtu',
  tons: 'Metric Tons',
  co2e: 'CO2e',
};

export const UTILITY_CURRENCY = {
  dollars: '$',
};

export const UTILITY_ENVIRONMENTAL_IMPACT_FACTORS = {
  kgCo2ePerGallonWater: 0.34,
  kgCo2ePerKWh: 0.433,
  kgCo2ePerMMBtu: 52.9,
  metricTonPerKg: 0.001,
  metricTonsCo2ePerTree: 0.06,
  metricTonsCo2ePerGallonGasoline: 8.887e-3,
  metricTonsCo2ePerMile: 3.9e-4,
  metricTonsCo2ePerHomeElectricityYearly: 5.139,
  kWhPerSmartphoneChargedYearly: 4.383,
  kWhPerHomeElectricityYearly: 11880,
  gallonsPerOlympicPool: 490000,
  mmBtuPerGasolinePoweredVehicleYearly: 84.6211835658,
};
